import {
  SHOPPING_API_DATE_FORMAT,
  getCancellationBeforeDate,
  getCancellationDateformat,
} from '@/app/components/hotel-detail/common/dates';
import { format, parse } from 'date-fns';
import type { TranslationFunction } from '../../translations/Translation';

const ROOM_OPTION_KEYS = {
  ACCOMODATION_ONLY: 'vacation.common.mealplantype.onlybed',
  ACCOMODATION_AND_BREAKFAST: 'vacation.common.mealplantype.bedandbreakfast',
  FULL_BOARD: 'vacation.common.mealplantype.fullboard',
  HALF_BOARD: 'vacation.common.mealplantype.halfboard',
  ALL_INCLUSIVE: 'vacation.common.mealplantype.allinclusive',
  REFUNDABLE_HO: 'vacation.common.mealplan.room.cancellationtype.refundable.title.ho',
  REFUNDABLE_DP: 'vacation.common.mealplan.room.cancellationtype.refundable.title.dp',
  NOT_REFUNDABLE: 'vacation.common.mealplan.room.cancellationtype.nonrefundable.title',
  UNKNOWN: 'vacation.common.mealplantype.others',
};

const BOARD_CLASS_CODES: { [key in BoardClassType]: string } = {
  FULL_BOARD: 'FB',
  HALF_BOARD: 'HB',
  ACCOMODATION_ONLY: 'BO',
  ALL_INCLUSIVE: 'AI',
  ACCOMODATION_AND_BREAKFAST: 'BB',
};

const CANCELLATION_POLICY_CODES: { [key in CancellationType]: string } = {
  NOT_REFUNDABLE: 'NR',
  REFUNDABLE: 'RF',
  UNKNOWN: 'NR',
};

export function getOptionKey(option: CancellationType | BoardClassType, accommodationOnly: boolean): string {
  return ROOM_OPTION_KEYS[option === 'REFUNDABLE' ? (accommodationOnly ? 'REFUNDABLE_HO' : 'REFUNDABLE_DP') : option];
}

export function getMealPlanCode(mealPlan: MealPlanType): string {
  return `${BOARD_CLASS_CODES[mealPlan.boardClass] ?? 'BO'}${CANCELLATION_POLICY_CODES[mealPlan.cancellationPolicy.type] ?? 'NR'}${mealPlan.cancellationPolicy.upTo ? mealPlan.cancellationPolicy.upTo.replaceAll('-', '') : ''}`;
}

export function getMealPlanByCode(mealPlans: readonly MealPlanType[], code: string): MealPlanType | undefined {
  return mealPlans.find((mealPlan) => getMealPlanCode(mealPlan) === code);
}

export function findMealPlan(
  configurations: readonly MealPlanType[] | undefined,
  mealPlanId?: string,
  mealPlanCode?: string
): MealPlanType | undefined {
  let selectedMealPlan = mealPlanId ? configurations?.find((mealplan) => mealplan.id === mealPlanId) : undefined;

  if (!selectedMealPlan) {
    const mealPlanByCode = mealPlanCode && configurations ? getMealPlanByCode(configurations, mealPlanCode) : undefined;
    selectedMealPlan = mealPlanByCode || configurations?.[0];
  }

  return selectedMealPlan;
}

export function isMealPlanInRooms(rooms: RoomType[] | undefined, selectedMealPlan: MealPlanType | undefined): boolean {
  if (!rooms || !selectedMealPlan) return false;
  return rooms.some((room) => room.configurations.some((cfg) => cfg.id === selectedMealPlan.id));
}

export function getExtraAmenities(
  t: TranslationFunction,
  maxOccupancy: number | null,
  distribution: RoomsDistribution | null
) {
  return [
    {
      type: 'SIZE',
      description: distribution?.size
        ? t('vacation.select.room.amenity.size', { size: distribution?.size.toString() })
        : '',
    },
    {
      type: 'MAX_OCCUPANCY',
      description: maxOccupancy
        ? maxOccupancy === 1
          ? t('vacation.select.room.amenity.max_occupancy_singular', { maxOccupancy: '1' })
          : t('vacation.select.room.amenity.max_occupancy_plural', { maxOccupancy: maxOccupancy.toString() })
        : '',
    },
    // TODO Review. From now we will show bedrooms quantity from the bed options
    // {
    //   type: 'BEDROOMS_QUANTITY',
    //   description: bedDistribution?.length
    //     ? bedDistribution.length === 1
    //       ? t('vacation.select.room.amenity.bedrooms_quantity_singular', { bedroomsQuantity: '1' })
    //       : t('vacation.select.room.amenity.bedrooms_quantity_plural', {
    //           bedroomsQuantity: bedDistribution.length.toString(),
    //         })
    //     : '',
    // },
    {
      type: 'BATH_QUANTITY',
      description: distribution?.bathQuantity
        ? distribution.bathQuantity === 1
          ? t('vacation.select.room.amenity.bath_quantity_singular')
          : t('vacation.select.room.amenity.bath_quantity_plural', {
              bathQuantity: distribution.bathQuantity.toString(),
            })
        : '',
    },
  ];
}

export function getMainAmenities(t: TranslationFunction, room: RoomType) {
  const extraAmenities = getExtraAmenities(t, room.maxOccupancy, room.distribution);
  return [
    ...extraAmenities.filter((amenity) => !!amenity.description),
    ...room.amenities.filter((amenity) => amenity.type !== 'GENERAL' && !!amenity.description),
  ];
}

// Gates the "see room info" link on the room panels/cards. MPH-778: a single description is
// enough — with `> 1`, a room with one description and no facilities rendered no link, leaving
// the details modal (the only place showing the description) unreachable. Blank-text entries
// don't count: the rooms adapter maps supply strings unfiltered, and the modal drops them.
export function hasRoomDetails(room: RoomType): boolean {
  return room.descriptions?.some((description) => description.text?.trim()) || room.amenities.length > 0;
}

export function getMealPlanCancellationPolicyLabel(
  selectedMealPlan: MealPlanType,
  accommodationOnly: boolean,
  t: TranslationFunction,
  locale: string
): string {
  return `${t(getOptionKey(selectedMealPlan.cancellationPolicy.type, accommodationOnly))}${
    selectedMealPlan.cancellationPolicy.type === 'REFUNDABLE' && selectedMealPlan.cancellationPolicy.upTo
      ? ` ${t('vacation.common.mealplan.room.cancellationtype.refundable.before', {
          date: format(
            parse(
              getCancellationBeforeDate(selectedMealPlan.cancellationPolicy.upTo),
              SHOPPING_API_DATE_FORMAT,
              new Date()
            ),
            getCancellationDateformat(locale)
          ),
        })}`
      : ''
  }`;
}

export function includesPayAtHotel(initialRooms: RoomType[]) {
  return initialRooms
    ?.map((room) => room.configurations.filter((meal) => meal.paymentType === 'PAY_AT_HOTEL'))
    .filter((room) => room.length).length;
}

export function shouldShowOneEuroDeferredPayments(rooms: RoomType[], currency: string) {
  return (
    ['GBP', 'EUR'].includes(currency) &&
    rooms.some((room) => room.configurations.some((configuration) => configuration.deferredPaymentDeposit === 1))
  );
}

export function shouldShowDeferredPayments(rooms: RoomType[]) {
  return rooms.some((room) => room.configurations.some((configuration) => !!configuration.deferredPaymentDeposit));
}

export function shouldShowMyPriceBanner(rooms: RoomType[]) {
  return rooms.some((room) => room.configurations.some((configuration) => !!configuration.isMyPrice));
}

export function setIndexOfRoomAndOptions(rooms: RoomType[]): RoomType[] {
  let mealPlanIndex = 0;
  return rooms.map((room, roomIndex) => ({
    ...room,
    configurations: room.configurations.map((mealPlan) => {
      mealPlanIndex += 1;
      return {
        ...mealPlan,
        index: mealPlanIndex,
      };
    }),
    index: roomIndex + 1,
  }));
}

export function hasFlashSalePromotion(mealPlan: MealPlanType | undefined): boolean {
  return mealPlan?.promotions?.some((p) => p.type?.toUpperCase() === 'FLASH_SALE') ?? false;
}

// Promotions that only affect the price and add no inclusions. They are never shown on the meal
// plan option label, so they must not split a board class into separate identical-looking options:
// both the option grouping (promotionKey, built server-side in the rooms API route) and the option
// label filtering (getPromotionsToShow) must rely on this same list.
export const PRICE_ONLY_PROMOTION_TYPES: readonly string[] = ['FLASH_SALE', 'BLACK_FRIDAY_OFFER'];

export function isPriceOnlyPromotion(promotion: { type?: string }): boolean {
  return PRICE_ONLY_PROMOTION_TYPES.includes(promotion.type?.toUpperCase() ?? '');
}

// The promotion info modal only adds value when the description says more than the label. The
// rooms API route falls back to the description for promotions arriving with an empty label
// (benefits/value adds contract), in which case the modal would just repeat the row text.
export function promotionHasExtraInfo(promotion: { label?: string; description?: string }): boolean {
  return !!promotion.description && promotion.description !== promotion.label;
}

export function isRoomsResponseError(
  roomsResponse: RoomsServiceResponseType | RoomsServiceResponseErrorType
): roomsResponse is RoomsServiceResponseErrorType {
  return (
    (roomsResponse as RoomsServiceResponseErrorType).errorType !== undefined ||
    (roomsResponse as RoomsServiceResponseType).selectedHotel?.rooms?.length === 0
  );
}

export function hasSameExcludedChargesInAllTheOffers(rooms: RoomType[]): boolean {
  if (!rooms || rooms.length === 0) {
    return false;
  }

  const allExcludedChargesAmounts = rooms.flatMap((room) =>
    room.configurations.map((configuration) => configuration.excludedCharges?.amount)
  );

  if (allExcludedChargesAmounts.length === 0) {
    return false;
  }

  const firstAmount = allExcludedChargesAmounts[0];
  return allExcludedChargesAmounts.every((amount) => amount === firstAmount);
}

export function shouldShowStrikeThroughPrice(mealPlan: MealPlanType | undefined, accommodationOnly: boolean): boolean {
  return (
    !!mealPlan?.strikeThroughPrice &&
    (accommodationOnly || mealPlan.isB2BDiscount || mealPlan.ratePriceType === 'LOYALTY')
  );
}

/**
 * Computes the price/taxes/total to display under Double Pricing (DMCC).
 *
 * Caller contract for `pricePerPerson` / `taxesPerPerson`:
 * - When `isDoublePriceDP=false` (adults-only DP): pass true per-person amounts. The helper
 *   returns them unchanged and sets `showPpSuffix=true` so the UI appends "/pp".
 * - When `isDoublePriceDP=true` (family DP): callers in this codebase already swap the
 *   per-person field to the group total upstream, so the multiplier here re-scales by
 *   `passengers` to keep all three call sites symmetric. Pass per-person amounts and the
 *   helper will produce group totals; `showPpSuffix=false` (no /pp suffix for families).
 */
export function getDoublePricingAmounts({
  pricePerPerson,
  taxesPerPerson,
  passengers,
  isDoublePriceDP,
  showTotalPrice = false,
}: {
  pricePerPerson: number;
  taxesPerPerson: number;
  passengers: number;
  isDoublePriceDP: boolean;
  showTotalPrice?: boolean;
}): { basePrice: number; taxes: number; total: number; showPpSuffix: boolean } {
  const showPerPerson = !isDoublePriceDP && !showTotalPrice;
  const multiplier = showPerPerson ? 1 : passengers;
  return {
    basePrice: pricePerPerson * multiplier,
    taxes: taxesPerPerson * multiplier,
    total: (pricePerPerson + taxesPerPerson) * multiplier,
    showPpSuffix: showPerPerson,
  };
}
